/* @jsx jsx */
import { useIsMobileView } from '@front10/utils';
import { useButton } from '@react-aria/button';
import { FocusScope } from '@react-aria/focus';
import { OverlayContainer, useOverlayPosition, useOverlayTrigger } from '@react-aria/overlays';
import { ListProps, useListState } from '@react-stately/list';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { Node } from '@react-types/shared';
import { jsx } from '@theme-ui/core';
import * as React from 'react';
import { MobileHeader } from './MobileHeader';
import { ApplyButton, ClearButton, FilterFooter, Summary } from './filters/FilterFooter';
import { useListFormatter } from './i18n/listFormatter';
import { ListBox as ContentPanel } from './Listbox';
import { ModalDialog } from './ModalDialog';
import { Popover } from './Popover';
import { PrimaryFilterButton } from './PrimaryFilterButton';
import { Stack } from './Stack';
import { useFiltersState } from './../../components/containers/PremiumExperienceGrid/Filters';
import { MultiSelectProps } from './../../components/containers/PremiumExperienceGrid/Filters';
import { useTranslation } from '@front10/utils';
import { Item } from '@react-stately/collections';
import { FilterButton } from './FilterButton';
import { Icon } from './Icon2';
import { Inline } from './Inline';
import { Text } from './Text';

interface Props<T extends object> {
  children: ListProps<T>['children'];
  disabledKeys?: ListProps<T>['disabledKeys'];
  onChange?: ListProps<T>['onSelectionChange'];
  defaultValue?: ListProps<T>['defaultSelectedKeys'];
  onClear?: () => void;
  label: string;
  showSearchOptions?: boolean;
  noResultLabel?: string;
  togglebuttonLabel?: string;
  selectAllOptions?: {
    label: string;
    value: string;
  };
  filter?: MultiSelectProps;
  panelId: string;
  secondaryFilter?: boolean;
  componentName?: string;
  alwaysEnabledDoneButton: boolean;
}

export interface BusinessRules {
  isDependedFilter?: boolean;
  relatedToParameterNameR2?: string;
  relatedToParameterNameR1?: string;
  relatedToValue?: string;
}

const ESC_KEY_CODE = 27;

const existItemOnArray = (item: string, array: string[]) => {
  return array.findIndex((i) => i === item) !== -1;
};

const isEnabledOption = (array: string[], array_: string[]) => {
  for (const element of array) {
    if (existItemOnArray(element, array_)) {
      return true;
    }
  }
  return false;
};

const getChildren = (
  children: any,
  businessRules: any,
  filtersState: any,
  options:
    | {
        label: string;
        value: string;
        experienceCategoriesRelated?: string[];
      }[]
    | undefined,
  translate: any,
  defaultValue: any
) => {
  let newDefaultValue: string[] = [];
  if (!filtersState) {
    return { children, defaultValue };
  }
  if (
    options &&
    options?.length !== 0 &&
    businessRules &&
    businessRules?.isDependedFilter &&
    businessRules?.relatedToParameterNameR1
  ) {
    const map = new Map();
    if (defaultValue && defaultValue.length !== 0) {
      defaultValue.forEach((element: any) => {
        map.set(element, true);
      });
    }
    const { relatedToParameterNameR1 } = businessRules;
    const selectedValues = filtersState[relatedToParameterNameR1];
    let optionsFilteredByBusinessRules: any = [];
    if (selectedValues && selectedValues.length !== 0) {
      for (let index = 0; index < options.length; index++) {
        const item = options[index];
        const experienceCategoriesRelated = options[index]?.experienceCategoriesRelated;
        if (experienceCategoriesRelated && experienceCategoriesRelated?.length !== 0) {
          if (isEnabledOption(selectedValues, experienceCategoriesRelated)) {
            optionsFilteredByBusinessRules.push(item);
            if (map.get(item.value)) {
              newDefaultValue.push(item.value);
            }
          }
        }
      }
      return {
        children: optionsFilteredByBusinessRules.map(
          (option: { label: string; value: string; experienceCategoriesRelated?: string[] }) => (
            <Item key={option.value}>{translate(option.label, option.label)}</Item>
          )
        ),
        defaultValue: newDefaultValue
      };
    } else {
      if (defaultValue && defaultValue.length !== 0) {
        newDefaultValue = [...defaultValue];
      }
    }
  } else {
    if (defaultValue && defaultValue.length !== 0) {
      newDefaultValue = [...defaultValue];
    }
  }
  return { children, defaultValue: newDefaultValue };
};

const listOptions: Partial<Intl.ListFormatOptions> = { style: 'narrow' };

export function MultiSelect<T extends object = object>({
  children,
  disabledKeys,
  label,
  onChange,
  onClear,
  defaultValue,
  filter,
  panelId,
  secondaryFilter = false,
  componentName = 'premium-experience-grid',
  alwaysEnabledDoneButton = true
}: Props<T>) {
  const {
    showSearchOptions = false,
    selectAllOptions = { label: '', value: '' },
    noResultLabel,
    togglebuttonLabel
  }: any = filter;
  let state = useOverlayTriggerState({});
  let [filterState, setFilterState] = React.useState<'clean' | 'dirty'>('clean');
  const translate = useTranslation();
  const state_ = useFiltersState((state) => state.state);
  const context = useFiltersState((state) => state.context);
  const filtersState: any = {
    offerType: context,
    ...state_[context]
  };
  const [saveState_, setSaveState_] = React.useState(() => {
    if (componentName === 'premium-experience-grid') {
      return state_;
    } else {
      return filtersState;
    }
  });
  const [isSelectedAllOptions, setIsSelectedAllOptions] = React.useState(false);
  const [isSelectedAllOptionsCopy, setIsSelectedAllOptionsCopy] = React.useState(false);
  // Create state based on the incoming props
  const { children: children_, defaultValue: defaultValue_ } = getChildren(
    children,
    filter?.businessRules,
    state_[panelId],
    filter?.options,
    translate,
    defaultValue
  );

  const listProps: ListProps<T> = {
    selectionMode: 'multiple',
    children: children_,
    disabledKeys,
    onSelectionChange: () => setFilterState('dirty'),
    defaultSelectedKeys: defaultValue_
  };
  let listState = { ...useListState(listProps), options: filter?.options };

  let listFormatter = useListFormatter(listOptions);
  const selection = listState.selectionManager;
  const [savedKeys, setSavedKeys] = React.useState(selection.selectedKeys);

  React.useEffect(() => {
    if (state.isOpen) {
      selection.setSelectedKeys(savedKeys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isOpen]);

  const clearField = useFiltersState((state) => state.clearField);

  const isMobile = useIsMobileView();
  const clearSelectedItems = (field: string, parameter: string) => {
    selection.clearSelection();
    setIsSelectedAllOptions(false);
    setIsSelectedAllOptionsCopy(false);
    setSavedKeys(new Set());
    if (panelId) {
      clearField(field, parameter);
    }
  };

  React.useEffect(() => {
    if (componentName === 'events-carousel') {
      if (state_ && saveState_ && filter?.id) {
        if (JSON.stringify(state_) !== JSON.stringify(saveState_)) {
          const panelState: any = filtersState;
          const panelStateOld: any = saveState_;
          const index: any = filter?.businessRules?.relatedToParameterNameR1;
          if (panelState && panelStateOld && index) {
            const valueActualState = panelState[index];
            const valueOldState = panelStateOld[index];
            if (JSON.stringify(valueActualState) !== JSON.stringify(valueOldState)) {
              clearSelectedItems(filter?.id, panelId);
            }
          }
        }
      }
      setSaveState_(filtersState);
    } else if (componentName === 'premium-experience-grid') {
      if (state_ && saveState_ && filter?.id) {
        if (JSON.stringify(state_) !== JSON.stringify(saveState_)) {
          const panelState: any = state_[panelId];
          const panelStateOld: any = saveState_[panelId];
          const index: any = filter?.businessRules?.relatedToParameterNameR1;
          if (panelState && panelStateOld && index) {
            const valueActualState = panelState[index];
            const valueOldState = panelStateOld[index];
            if (JSON.stringify(valueActualState) !== JSON.stringify(valueOldState)) {
              clearSelectedItems(filter?.id, panelId);
            }
          }
        }
      }
      setSaveState_(state_);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state_]);

  React.useEffect(() => {
    if (componentName === 'events-carousel') {
      if (state_ && saveState_ && filter?.id) {
        if (JSON.stringify(state_) !== JSON.stringify(saveState_)) {
          const panelState: any = filtersState;
          const panelStateOld: any = saveState_;
          const index: any = filter?.businessRules?.relatedToParameterNameR2;
          const relatedToValue = filter?.businessRules?.relatedToValue;
          if (panelState && panelStateOld && index) {
            const valueActualState = panelState[index];
            const valueOldState = panelStateOld[index];
            if (JSON.stringify(valueActualState) !== JSON.stringify(valueOldState)) {
              if (
                valueActualState &&
                valueActualState.length !== 0 &&
                valueActualState[0] === relatedToValue
              ) {
                clearSelectedItems(filter?.id, panelId);
              }
            }
          }
        }
      }
      setSaveState_(filtersState);
    } else if (componentName === 'premium-experience-grid') {
      if (state_ && saveState_ && filter?.id) {
        if (JSON.stringify(state_) !== JSON.stringify(saveState_)) {
          const panelState: any = state_[panelId];
          const panelStateOld: any = saveState_[panelId];
          const index: any = filter?.businessRules?.relatedToParameterNameR2;
          const relatedToValue = filter?.businessRules?.relatedToValue;
          if (panelState && panelStateOld && index) {
            const valueActualState = panelState[index];
            const valueOldState = panelStateOld[index];
            if (JSON.stringify(valueActualState) !== JSON.stringify(valueOldState)) {
              if (
                valueActualState &&
                valueActualState.length !== 0 &&
                valueActualState[0] === relatedToValue
              ) {
                clearSelectedItems(filter?.id, panelId);
              }
            }
          }
        }
      }
      setSaveState_(state_);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state_]);

  const isDisabledMultiSelectFilter = (
    { isDependedFilter, relatedToParameterNameR2, relatedToValue }: BusinessRules,
    filtersState: any
  ) => {
    if (
      isDependedFilter === true &&
      relatedToParameterNameR2 &&
      filtersState[relatedToParameterNameR2] &&
      filtersState[relatedToParameterNameR2].length === 1 &&
      relatedToValue
    ) {
      return filtersState[relatedToParameterNameR2][0] === relatedToValue;
    }
  };

  let triggerRef = React.useRef<any>(null);
  let popoverRef = React.useRef(null);

  let overlayState = useOverlayTriggerState({
    isOpen: state.isOpen
  });

  // Get props for the trigger and overlay. This also handles
  // hiding the overlay when a parent element of the trigger scrolls
  // (which invalidates the popover positioning).
  let { triggerProps } = useOverlayTrigger({ type: 'dialog' }, overlayState, triggerRef);

  // Get popover positioning props relative to the trigger
  let { overlayProps: positionProps } = useOverlayPosition({
    targetRef: triggerRef,
    overlayRef: popoverRef,
    placement: 'bottom left',
    offset: 0,
    isOpen: state.isOpen
  });

  // useButton ensures that focus management is handled correctly,
  // across all browsers. Focus is restored to the button once the
  // popover closes.
  let { buttonProps } = useButton(
    {
      onPress: () => state.open(),
      elementType: 'div'
    },
    triggerRef
  );

  const handleClose = () => {
    if (triggerRef && triggerRef?.current) {
      triggerRef?.current?.focus();
    }
    setFilterState('clean');
    state.close();
  };

  const handleDismiss = () => {
    selection.setSelectedKeys(savedKeys);
    setIsSelectedAllOptions(isSelectedAllOptionsCopy);
    handleClose();
  };

  const handleSave = () => {
    setSavedKeys(selection.selectedKeys);
    onChange?.(selection.selectedKeys);
    setIsSelectedAllOptionsCopy(isSelectedAllOptions);
    handleClose();
  };

  const handleClear = () => {
    selection.clearSelection();
    setIsSelectedAllOptions(false);
    onClear?.();
  };

  const count = savedKeys.size;
  const computedLabel = React.useMemo(() => {
    function getSelectedItems(keys: Set<React.Key>) {
      const items: Node<T>[] = [];
      keys.forEach((key) => {
        const defaultAdd = listState.collection.getItem(key);
        defaultAdd && items.push(defaultAdd);
      });
      return items;
    }

    const selectedItems = getSelectedItems(savedKeys);
    return count === 0
      ? translate('cmp-filter-label-any', 'Any')
      : listFormatter.format(selectedItems.map((node) => node.textValue));
  }, [count, listFormatter, listState.collection, savedKeys, translate]);

  const content = (
    <Stack as="section" sx={{ height: '100%' }}>
      <div sx={{ flex: 1 }}>
        <ContentPanel
          {...listProps}
          aria-label={label}
          state={listState}
          showSearchOptions={showSearchOptions}
          selectAllOptions={selectAllOptions}
          isSelectedAllOptions={isSelectedAllOptions}
          setIsSelectedAllOptions={setIsSelectedAllOptions}
          noResultLabel={noResultLabel}
        />
      </div>
      <Footer
        onSave={handleSave}
        onClear={handleClear}
        count={selection.selectedKeys.size}
        isClearDisabled={selection.isEmpty}
        isApplyDisabled={
          alwaysEnabledDoneButton
            ? false
            : filterState === 'clean' && selection.selectedKeys.size === 0
        }
      />
    </Stack>
  );

  return (
    <React.Fragment>
      {secondaryFilter ? (
        <FilterButton
          ref={triggerRef}
          variant="toggleButton"
          {...buttonProps}
          {...triggerProps}
          onKeyDown={(e: any) => {
            if (e.keyCode === ESC_KEY_CODE && state.isOpen) {
              handleDismiss();
            }
            if (buttonProps?.onKeyDown) {
              buttonProps?.onKeyDown(e);
            }
          }}
          dataOptions={`data-options-${savedKeys.size}`}
          tabIndex={0}
          disabled={isDisabledMultiSelectFilter(filter?.businessRules ?? {}, filtersState)}
        >
          <Inline gap={1} sx={{ flex: 1 }}>
            <Text as="span" variant="toggleButton.label">
              {savedKeys.size ? computedLabel : translate(togglebuttonLabel, 'filter')}
            </Text>
          </Inline>
          {count > 1 ? <span>({count})</span> : null}
          <Icon aria-hidden="true" icon="keyboard_arrow_down" size={20} />
        </FilterButton>
      ) : (
        <PrimaryFilterButton
          {...buttonProps}
          {...triggerProps}
          label={translate(label, label)}
          value={computedLabel}
          count={count > 1 ? count : null}
          ref={triggerRef}
          disabled={isDisabledMultiSelectFilter(filter?.businessRules ?? {}, filtersState)}
        />
      )}
      {state.isOpen && (
        <OverlayContainer>
          <FocusScope restoreFocus autoFocus contain={isMobile}>
            {isMobile ? (
              <ModalDialog isOpen size="stretch" onClose={handleDismiss}>
                <Stack alignY="space-between" sx={{ height: '100%' }}>
                  <MobileHeader onBackButtonPress={handleDismiss} title={label} />
                  {content}
                </Stack>
              </ModalDialog>
            ) : (
              <Popover
                {...positionProps}
                popoverRef={popoverRef}
                isOpen={state.isOpen}
                onClose={handleDismiss}
              >
                {content}
              </Popover>
            )}
          </FocusScope>
        </OverlayContainer>
      )}
    </React.Fragment>
  );
}

interface FooterProps {
  onClear: () => void;
  onSave: () => void;
  count: number;
  isClearDisabled: boolean;
  isApplyDisabled: boolean;
}

function Footer({ onSave, onClear, count, isClearDisabled, isApplyDisabled }: FooterProps) {
  const applyButtonRef = React.useRef<any>(null);
  const handleClear = () => {
    onClear();
    if (applyButtonRef?.current && applyButtonRef?.current?.focus) {
      applyButtonRef?.current?.focus();
    }
  };
  return (
    <FilterFooter
      infoSlot={<Summary count={count} />}
      actionsSlot={
        <React.Fragment>
          <ClearButton onPress={handleClear} isDisabled={isClearDisabled} />
          <ApplyButton onPress={onSave} isDisabled={isApplyDisabled} ref={applyButtonRef} />
        </React.Fragment>
      }
    />
  );
}
